import type {Container} from "konva/lib/Container.js";
import React, {useEffect, useRef, useState} from "react";
import {Image, Layer, Line, Rect, Stage, Transformer} from "react-konva";
import useImage from "use-image";
import {z} from "zod";
import {EditLayer} from "./components/EditLayer.js";
import {Shapes} from "./components/Shapes.js";
import {ToolBox} from "./components/ToolBox.js";

// Define the schema for the Free type
const FreeSchema = z.object({
  x: z.number(),
  y: z.number(),
  points: z.array(z.number()),
  label: z.string(),
  color: z.string(),
});

// Define the schema for the Square type
const SquareSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  label: z.string(),
  color: z.string(),
});

// Define the schema for the Annotation type (union of Free and Square)
export const AnnotationSchema = z.union([FreeSchema, SquareSchema]);

export type Annotation = z.infer<typeof AnnotationSchema>;
export type FreeAnnotation = z.infer<typeof FreeSchema>;
export type SquareAnnotation = z.infer<typeof SquareSchema>;

export function isSquareAnnotation(a: Annotation): a is SquareAnnotation {
  return "width" in a;
}

type Props = {
  title: string;
  values: Annotation[];
  imagePath: string;
  onChange: (annotations: Annotation[]) => void;
  onUpdateImage: (dataUrl: string) => void;
  disabled: boolean;
  closing: boolean;
};

export function ImageAnnotation({
  title,
  disabled,
  values,
  imagePath,
  onChange,
  onUpdateImage,
  closing,
}: Props) {
  const [image] = useImage(imagePath);
  const [newValue, setNewValue] = useState<Annotation | null>(null);
  const [currentColor, setCurrentColor] = useState<string>("#785bc7");
  const [currentTool, setCurrentTool] = useState<"square" | "free" | null>("free");
  const [{width, height}, setSize] = useState({width: 300, height: 200});
  const [editingText, setEditingText] = useState<{index: number} | null>(null);
  const stageRef = useRef<Container | null>(null);
  const transformerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (closing) {
      setEditingText(null);
      handleUpdateImage();
    }
  }, [closing]);

  useEffect(() => {
    if (editingText !== null && transformerRef.current) {
      const node = stageRef.current?.findOne(`#annotation-${editingText.index}`);
      if (node) {
        transformerRef.current.nodes([node]);
        transformerRef.current.getLayer().batchDraw();
      }
    }
  }, [editingText]);
  React.useEffect(() => {
    // gets the size and updates state
    function updateSize() {
      if (!containerRef.current) {
        return;
      }
      const {width, height} = containerRef.current.getBoundingClientRect();
      let finalHeight = height;

      if (image) {
        finalHeight = (image.height * width) / image.width;
      }
      setSize({
        width,
        height: finalHeight,
      });
      handleUpdateImage();
    }
    setTimeout(() => {
      updateSize();
    }, 100);

    // event listener
    window.addEventListener("resize", updateSize);
    // remove the event listener before the component gets unmounted
    return () => window.removeEventListener("resize", updateSize);
  }, [image]);

  if (!image) {
    return <div>Loading image...</div>;
  }

  let globalCursor = disabled ? "not-allowed" : "crosshair";
  if (!currentTool) {
    globalCursor = "grab";
  }
  return (
    <>
      <p style={{marginBottom: "24px"}}>
        <strong>{title}</strong>
      </p>

      <ToolBox
        disabled={disabled}
        onColorChange={(color) => {
          if (color === null) {
            return;
          }
          setCurrentColor(color as string);
        }}
        currentColor={currentColor}
        onToolChange={setCurrentTool}
        currentTool={currentTool}
      />
      <div
        ref={containerRef}
        style={{
          marginBottom: "20px",
          width: "100%",
          overflow: "hidden",
          touchAction: "none",
          position: "relative",
        }}
      >
        {editingText !== null && !disabled && values[editingText.index] && (
          <EditLayer
            width={width}
            height={height}
            annotation={values[editingText.index] as Annotation}
            onTextEdit={handleTextEdit}
            onTextEdited={finishTextEdit}
            onDelete={handleDeleteAnnotation}
          />
        )}
        <Stage
          width={width}
          height={height}
          onMouseDown={handleStageMouseDown}
          onTouchStart={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onTouchMove={handleStageTouchMove}
          onMouseUp={handleStageMouseUp}
          onTouchEnd={handleStageMouseUp}
          ref={stageRef as any}
          style={{cursor: globalCursor}}
        >
          <Layer padding={100}>
            {image && <Image image={image} width={width} height={height} />}
            <Shapes
              width={width}
              height={height}
              values={values}
              editingIndex={editingText?.index}
              disabled={disabled}
              stageRef={stageRef}
              onChange={onChange}
              onSelect={handleSelect}
            />

            {newValue && isSquareAnnotation(newValue) && (
              <Rect
                x={newValue.x}
                y={newValue.y}
                width={newValue.width}
                height={newValue.height}
                stroke={newValue.color}
                strokeWidth={2}
                dash={[5, 5]}
              />
            )}

            {newValue && !isSquareAnnotation(newValue) && (
              <Line
                x={newValue.x}
                y={newValue.y}
                points={newValue.points}
                stroke={newValue.color}
                strokeWidth={2}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
              />
            )}

            {editingText !== null && !disabled && <Transformer ref={transformerRef} />}
          </Layer>
        </Stage>
      </div>
    </>
  );

  function handleSelect(index: number) {
    if (disabled) {
      return;
    }
    setEditingText({
      index,
    });
  }

  function getRelativePointerPosition(stage: any) {
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    const pos = stage.getPointerPosition();
    return transform.point(pos);
  }

  function handleStageMouseDown(e: any) {
    if (disabled) {
      return;
    }
    if (transformerRef.current?.isTransforming()) {
      return;
    }
    if (transformerRef.current?.isDragging()) {
      return;
    }

    if (editingText !== null && editingText) {
      setEditingText(null);
      handleUpdateImage();
      return;
    }
    if (currentTool === null) {
      return;
    }
    if (currentColor === null) {
      return;
    }
    const stage = e.target.getStage();
    // Check if clicking on the stage itself
    if (e.target.index === e.target.getStage().index) {
      const pos = getRelativePointerPosition(stage);
      if (currentTool === "square") {
        setNewValue({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          color: currentColor,
          label: `Zone ${values.length + 1}`,
        });
      } else if (currentTool === "free") {
        setNewValue({
          x: 0,
          y: 0,
          points: [pos.x, pos.y],
          color: currentColor,
          label: `Zone ${values.length + 1}`,
        });
      }
    }
  }

  function handleStageMouseMove(e: any) {
    if (disabled) {
      return;
    }

    const stage = e.target.getStage();
    const pos = getRelativePointerPosition(stage);

    if (newValue) {
      if ("points" in newValue) {
        setNewValue({
          ...newValue,
          points: [...newValue.points, pos.x, pos.y],
        });
      }
      if (isSquareAnnotation(newValue)) {
        setNewValue({
          ...newValue,
          width: pos.x - (newValue.x || 0),
          height: pos.y - (newValue.y || 0),
        });
      }
    }
  }

  function handleStageTouchMove(e: any) {
    if (disabled || !newValue) {
      return;
    }
    e.evt.preventDefault();
    handleStageMouseMove(e);
  }

  function handleStageMouseUp() {
    if (disabled) {
      return;
    }
    if (newValue === null) {
      return;
    }
    const value = {...newValue};
    if (isSquareAnnotation(value)) {
      if (value.height < 0) {
        value.y = value.y + value.height;
        value.height = -value.height;
      }
      if (value.width < 0) {
        value.x = value.x + value.width;
        value.width = -value.width;
      }
    }
    const newValues = [...values, value];
    onChange(newValues);
    setNewValue(null);
    setCurrentTool(null);
    handleUpdateImage();
    const index = newValues.length - 1;
    setEditingText({
      index,
    });
  }

  function handleTextEdit(e: React.ChangeEvent<HTMLInputElement>) {
    if (!editingText) {
      return;
    }

    const updatedAnnotations = values.map((ann, i) => {
      if (i === editingText.index) {
        return {...ann, label: e.target.value};
      }
      return ann;
    });
    onChange(updatedAnnotations);
  }

  function finishTextEdit() {
    if (!editingText) {
      return;
    }
    setEditingText(null);
    handleUpdateImage();
  }

  function handleDeleteAnnotation() {
    if (disabled) {
      return;
    }
    if (!editingText) {
      return;
    }

    onChange(values.filter((_, i) => i !== editingText.index));
    setEditingText(null);
    handleUpdateImage();
  }

  function handleUpdateImage() {
    setTimeout(() => {
      if (!stageRef.current) {
        return;
      }
      const dataUrl = stageRef.current.toDataURL();
      onUpdateImage(dataUrl);
    }, 50);
  }
}
